import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'
import storage from 'redux-persist-indexeddb-storage'
import farmsReducer from './farms'
import poolsReducer from './pools'
import profileReducer from './profile'
import affiliateReducer from './affiliate'
import referralsReducer from './referrals'
import infoReducer from './info'
import { updateVersion } from './global/actions'
import user from './user/reducer'
import transactions from './transactions/reducer'
import swap from './swap/reducer'
import mint from './mint/reducer'
import lists from './lists/reducer'
import burn from './burn/reducer'
import multicall from './multicall/reducer'

const PERSISTED_KEYS: string[] = ['user', 'transactions', 'lists']

const persistConfig = {
  key: 'root',
  whitelist: PERSISTED_KEYS,
  storage: storage('Nomiswap'),
  version: 1,
}

const persistedReducer = persistReducer(
  persistConfig,
  combineReducers({
    affiliate: affiliateReducer,
    farms: farmsReducer,
    pools: poolsReducer,
    profile: profileReducer,
    referrals: referralsReducer,
    info: infoReducer,

    // Exchange
    user,
    transactions,
    swap,
    mint,
    burn,
    multicall,
    lists,
  }),
)

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        // Ignore BigInt values in multicall state paths
        ignoredPaths: ['multicall.callResults'],
        // Don't warn about BigInt values
        warnAfter: 128,
      },
    }),
  devTools: process.env.NODE_ENV === 'development',
})

/**
 * @see https://redux-toolkit.js.org/usage/usage-with-typescript#getting-the-dispatch-type
 */
export type AppDispatch = typeof store.dispatch
export type AppState = ReturnType<typeof store.getState>
export const useAppDispatch = () => useDispatch<AppDispatch>()

export const persistor = persistStore(store, undefined, () => {
  store.dispatch(updateVersion())
})

export default store
